import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";

import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import { useActiveLocationTimezone } from "@/hooks/use-locations";
import { notify, useAppStore } from "@/store/store";
import type { ApiQuoteRequest, PaginatedResponse } from "@/types/api";

function useScope() {
  return {
    isAuthenticated: useAuthStore((s) => s.isAuthenticated),
    organizationId: useAuthStore((s) => s.organizationId),
    locationId: useAuthStore((s) => s.locationId),
  };
}

export function useCotizaciones(filters?: { status?: string }) {
  const { isAuthenticated, organizationId, locationId } = useScope();
  return useQuery({
    queryKey: ["cotizaciones", organizationId, locationId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.set("status", filters.status);
      const qs = params.toString();
      const res = await api
        .get(`quote-requests/${qs ? `?${qs}` : ""}`)
        .json<PaginatedResponse<ApiQuoteRequest>>();
      return res.items;
    },
    enabled: isAuthenticated && !!organizationId && !!locationId,
  });
}

function useHoldMutation(path: (id: string) => string) {
  const queryClient = useQueryClient();
  const { organizationId } = useScope();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body?: Record<string, unknown> }) =>
      api.post(path(id), { json: body ?? {} }).json<ApiQuoteRequest>(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["cotizaciones", organizationId] }),
  });
}

export const useDefineHold = () => useHoldMutation((id) => `quote-requests/${id}/hold`);
export const useReleaseHold = () => useHoldMutation((id) => `quote-requests/${id}/hold/release`);
export const useExtendHold = () => useHoldMutation((id) => `quote-requests/${id}/hold/extend`);

export function useSetQuoteStatus() {
  const queryClient = useQueryClient();
  const { organizationId } = useScope();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { status: string } }) =>
      api.post(`quote-requests/${id}/status`, { json: body }).json<ApiQuoteRequest>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cotizaciones", organizationId] });
    },
  });
}

/** Shared actions for Reservar / Descartar / Extender — used by both list row and detail dialog. */
export function useQuoteActions(onDiscartar?: () => void) {
  const extendHold = useExtendHold();
  const setStatus = useSetQuoteStatus();
  const navigate = useNavigate();
  const locationTz = useActiveLocationTimezone();

  function reservar(quote: ApiQuoteRequest) {
    // Don't release the hold on click — the hold stays active and the
    // quote stays "En Hold" until the reservation is actually saved.
    // The backend converts the quote (and releases the hold) on create
    // when the payload carries ``quote_request_id``.
    const holdSlot = quote.hold
      ? locationTz
        ? dayjs(quote.hold.starts_at).tz(locationTz)
        : dayjs(quote.hold.starts_at)
      : null;
    const s = useAppStore.getState();
    s.setDetailReservationId(null);
    s.openNewReservation({
      guestName: quote.guest_name,
      guestPhone: quote.guest_phone ?? "",
      guestEmail: quote.guest_email ?? "",
      pax: quote.pax,
      date: holdSlot ? holdSlot.format("YYYY-MM-DD") : (quote.tentative_date ?? ""),
      time: holdSlot ? holdSlot.format("h:mm A") : "",
      // Pre-select the hold's tables so the booking already carries them
      // and "Crear reserva" is enabled (vs "Crear sin mesa").
      tableIds: quote.hold?.table_ids ?? [],
    });
    // After openNewReservation (which clears it) flag this quote so the
    // Sidebar threads its id into the create payload.
    useAppStore.getState().setConvertingQuoteId(quote.id);
    s.setShowSidebar(true);
    navigate({ to: "/" });
  }

  function descartar(quote: ApiQuoteRequest) {
    if (!window.confirm("¿Descartar esta cotización? Esto libera el hold.")) return;
    setStatus.mutate(
      { id: quote.id, body: { status: "dismissed" } },
      {
        onSuccess: () => {
          notify("success", "Cotización descartada", "");
          onDiscartar?.();
        },
      },
    );
  }

  function extend(quoteId: string, hours: number) {
    extendHold.mutate(
      { id: quoteId, body: { hours } },
      { onSuccess: () => notify("success", "Hold extendido", "") },
    );
  }

  return { reservar, descartar, extend };
}

/** Derive display state for a quote. */
export function deriveEstado(
  quote: ApiQuoteRequest,
): "Reservada" | "Descartada" | "En Hold" | "Pendiente" {
  if (quote.status === "converted") return "Reservada";
  if (quote.status === "dismissed") return "Descartada";
  if (quote.hold?.status === "active") return "En Hold";
  return "Pendiente";
}
